import { useState, type ReactNode } from 'react';

import { ChevronDownIcon } from '../../layout/icons';
import { DonutRow } from './ExamCharts';
import { ExamHtml } from './ExamStudentSheet';
import { correctPercent, STATUS_COLORS, wrongPercent, type ExamQuestionStat } from './examResults';

const WRONG_COLOR = '#EF4444';

function share(count: number, total: number): string {
  return `${total > 0 ? (count / total) * 100 : 0}%`;
}

/** What the class picked on one question: option bars, or Đúng / Sai / Bỏ trống for a fill-in. */
function QuestionBreakdown({ question }: { question: ExamQuestionStat }) {
  const answered = question.countCorrectSelections + question.countWrongSelections;
  return (
    <div className="results-question__detail">
      <div className="quiz-results__stem">{question.stemHtml ? <ExamHtml html={question.stemHtml} /> : question.stem || 'Câu này không có nội dung chữ.'}</div>
      {answered + question.blank === 0 ? <p className="quiz-results__empty">Chưa có học sinh nào trả lời câu này.</p> : question.options.length > 0 ? question.options.map((option) => {
        const percent = answered > 0 ? Math.round((option.count / answered) * 100) : 0;
        return (
          <div key={option.key} className="quiz-results__option">
            <span className={option.correct ? 'quiz-results__option-badge quiz-results__option-badge--correct' : 'quiz-results__option-badge'}>{option.label}</span>
            <span className="quiz-results__option-body">
              <span className="quiz-results__option-head">
                <span className="quiz-results__option-text">{option.text || /<img\b/i.test(option.html) ? <ExamHtml html={option.html} /> : (option.correct ? 'Đáp án đúng' : '')}</span>
                <span className="quiz-results__option-count">{option.count} / {answered}</span>
              </span>
              <span className="quiz-results__option-track"><span className={option.correct ? 'quiz-results__option-fill quiz-results__option-fill--correct' : 'quiz-results__option-fill'} style={{ width: `${percent}%` }} /></span>
            </span>
            <span className={option.correct ? 'quiz-results__option-percent quiz-results__option-percent--correct' : 'quiz-results__option-percent'}>{percent}%</span>
          </div>
        );
      }) : (
        <DonutRow slices={[
          { title: 'Đúng', count: question.countCorrectSelections, color: STATUS_COLORS.done },
          { title: 'Sai', count: question.countWrongSelections, color: WRONG_COLOR },
          { title: 'Bỏ trống', count: question.blank, color: STATUS_COLORS.idle },
        ]} />
      )}
      {question.options.length > 0 && question.blank > 0 ? <p className="quiz-results__empty">{question.blank} học sinh bỏ trống câu này.</p> : null}
    </div>
  );
}

export interface ExamQuestionsListProps {
  questions: ExamQuestionStat[];
  /** Spinner or retry while the questions load. */
  questionNote: ReactNode;
}

// Theo câu hỏi: each question's đúng / sai / bỏ trống; tapping one opens what the class picked
export function ExamQuestionsList({ questions, questionNote }: ExamQuestionsListProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (questions.length === 0) return <section className="quiz-results__block">{questionNote || <p className="quiz-results__empty">Bài kiểm tra này chưa có câu nào.</p>}</section>;
  return (
    <section className="quiz-results__block">
      {questionNote}
      <ul className="results-list">
        {questions.map((question) => {
          const answered = question.countCorrectSelections + question.countWrongSelections + question.blank;
          const open = question.id === openId;
          return (
            <li key={question.id} className={open ? 'results-question is-open' : 'results-question'}>
              <button type="button" className="results-question__row" aria-expanded={open} onClick={() => setOpenId(open ? null : question.id)}>
                <span className="results-question__head">
                  <span className="results-question__no">Câu {question.index + 1}</span>
                  <span className="results-question__pct"><strong>{correctPercent(question)}%</strong> đúng · {wrongPercent(question)}% sai</span>
                  <span className="results-question__chevron" aria-hidden={true}><ChevronDownIcon size={14} /></span>
                </span>
                <span className="results-question__bar" aria-hidden={true}>
                  <span style={{ width: share(question.countCorrectSelections, answered), background: STATUS_COLORS.done }} />
                  <span style={{ width: share(question.countWrongSelections, answered), background: WRONG_COLOR }} />
                </span>
                <span className="results-question__counts">{question.countCorrectSelections} đúng · {question.countWrongSelections} sai · {question.blank} bỏ trống</span>
              </button>
              {open ? <QuestionBreakdown question={question} /> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
